/**
 * Expression evaluation — a tree-walk over `ScreenerExpr`.
 *
 * **No parser ships to the browser.** This evaluates the same typed tree the
 * server does; turning DSL *text* into that tree is the compiler's job and stays
 * server-side. Printing a tree back to text is only a printer, which is why the
 * builder's DSL preview works without one (see `printExpr`).
 *
 * Two evaluators therefore exist — this one and the Polars lowering — and they
 * must agree. `contract/examples/expr-conformance.json` is the pin: one fixture
 * of ASTs and rows, asserted equal from both. Any semantic decision made here is
 * a decision the server has to match, so the awkward cases are settled
 * deliberately rather than falling out of the implementation:
 *
 * - **Nulls propagate.** A null operand yields null, never 0. An averaged null is
 *   a wrong number; a propagated null is visibly absent.
 * - **Divide-by-zero is not an error.** `close / 0` yields `Infinity`, matching
 *   Polars, and the grid must sort that without breaking.
 * - **Unknown fields and functions return null and are reported.** Throwing would
 *   let one bad expression blank an entire grid.
 * - **Series functions are reported, not rejected.** A screener value *is* the
 *   last bar of a series, so `ema(close, 20)` is legal DSL — it simply cannot be
 *   evaluated against a single row. That is a placement fact the server acts on,
 *   not a mistake the user made.
 */

import type {
  ScreenerExpr,
  ScreenerExprBinary,
  ScreenerExprCall,
  ScreenerExprField,
  ScreenerExprLiteral,
} from "@qufolio/model";
import type { ScreenerRow, ScreenerValue } from "@qufolio/transport";

/** Scalar functions legal in a screener row. */
export const SCALAR_FUNCTIONS = [
  "iff",
  "abs",
  "round",
  "min",
  "max",
  "log10",
  "coalesce",
] as const;

/**
 * Series functions — legal anywhere in the language, but they read bar history,
 * and a screener row carries only the last bar.
 *
 * **This set decides where a column is computed, so a name missing from it is a
 * blank column, not a worse error message.** `needsHistory` reads it; a name it
 * has not heard of is judged client-evaluable, gets a `valueGetter`, is
 * evaluated against a single row, and is never sent to the server — computed in
 * neither place. That is why `contract/examples/dsl-functions.json` exists and
 * why a test asserts this set equals it exactly.
 *
 * The catalog injected by the host (`/dsl/catalog`) is authoritative for what
 * can be *typed*; this literal is what the island knows with nothing injected,
 * and evaluation cannot wait for a fetch.
 *
 * Multi-output indicators appear once per output — `macdSignal`, `bbandsLower`,
 * `stochD` — because that is how the AST names them. See the header of
 * `services/quevee/dsl/functions.py` for why a selector field on the call node
 * was rejected: `printExpr` below would silently drop it.
 */
export const SERIES_FUNCTIONS = new Set([
  "ac",
  "accruals",
  "adl",
  "adx",
  "ageSince",
  "alligator",
  "alligatorJaw",
  "alligatorLips",
  "alligatorTeeth",
  "alma",
  "ao",
  "argHighest",
  "argLowest",
  "aroon",
  "aroonDown",
  "aroonOsc",
  "aroonUp",
  "assetTurn",
  "ath",
  "atr",
  "averagePrice",
  "barsCount",
  "barsSince",
  "bbands",
  "bbandsLower",
  "bbandsMiddle",
  "bbandsPercentB",
  "bbandsUpper",
  "bbandsWidth",
  "bearPower",
  "bop",
  "bullPower",
  "cci",
  "ce",
  "ceBuy",
  "ceDir",
  "ceLong",
  "ceSell",
  "ceShort",
  "chaikinOsc",
  "chaikinVolatility",
  "chandeKrollLong",
  "chandeKrollShort",
  "chandeKrollStop",
  "chikou",
  "chop",
  "cmf",
  "cmo",
  "connorsRsi",
  "coppock",
  "crossOver",
  "crossUnder",
  "composite",
  "compositeRaw",
  "cum",
  "currentRatio",
  "debtToEquity",
  "dema",
  "divYield",
  "dividendPerShare",
  "donchian",
  "donchianLower",
  "donchianMiddle",
  "donchianUpper",
  "earningsYield",
  "earningsLift",
  "ebitda",
  "elderRay",
  "ema",
  "envelope",
  "envelopeLower",
  "envelopeMiddle",
  "envelopeUpper",
  "epsRank",
  "ergRank",
  "eom",
  "eps",
  "consistency",
  "epsAvgGrowth5y",
  "eps5yGrowthPct",
  "epsRateYoY",
  "epsCagr3y",
  // The growth ranks and the revenue surprise (2026-10-09), struck per report.
  "epsQoQRank",
  "revQoQRank",
  "revSurprise",
  // Reported EPS/Rev growth as a series, one value per report (2026-10-29).
  "epsYoYReported",
  "epsQoQReported",
  "revYoYReported",
  "revQoQReported",
  "evEbitda",
  "evSales",
  "fcfMargin",
  "fcfPayout",
  "fcfYield",
  "fisher",
  "fisherSignal",
  "forceIndex",
  "freeCashFlow",
  "garmanKlass",
  "grossMargin",
  "grossProfit",
  "growthBox",
  "grpRank",
  "haClose",
  "haHigh",
  "haLow",
  "haOpen",
  "heikinAshi",
  "highest",
  "hma",
  "hv",
  "ichimoku",
  "industryStrength",
  "insideBar",
  "interestCoverage",
  "inventoryTurnover",
  "kama",
  "keltner",
  "keltnerLower",
  "keltnerMiddle",
  "keltnerUpper",
  "kijun",
  "klinger",
  "klingerSignal",
  "kst",
  "kstSignal",
  "linreg",
  "linregIntercept",
  "linregSlope",
  "lowest",
  "macd",
  "macdHist",
  "macdSignal",
  "massIndex",
  "mcginley",
  "medianPrice",
  "mfi",
  "minusDi",
  "mom",
  "netDebtToEbitda",
  "netIncome",
  "netMargin",
  "netVolume",
  "nvi",
  "obv",
  "operatingIncome",
  "operatingMargin",
  "outsideBar",
  "pa",
  "paDownBars",
  "paSignal",
  "paUpBars",
  "paceRaw",
  "parkinson",
  "payoutRatio",
  "pb",
  "pe",
  "pePremium",
  "peRangePos",
  "peg",
  "pegy",
  "percentRank",
  "pfcf",
  "pivot",
  "pivotP",
  "pivotR1",
  "pivotR2",
  "pivotR3",
  "pivotS1",
  "pivotS2",
  "pivotS3",
  "plusDi",
  "powerOf3",
  "ppo",
  "ppoHist",
  "ppoSignal",
  "ps",
  "psar",
  "psarDir",
  "psarDown",
  "psarUp",
  "pvi",
  "pvt",
  "quickRatio",
  "rRank9M",
  "rRankA",
  "rRankAnchored",
  "rRankB",
  "rRankW",
  "rank9M",
  "rank9MBelowSlowMA",
  "rank9MSlowMA",
  "rankA",
  "rankAnchored",
  "range",
  "rankB",
  "rankW",
  "relativeVolatility",
  "revenue",
  "rma",
  "roa",
  "roc",
  "roce",
  "roe",
  "rsRankI",
  "rsi",
  "rvgi",
  "rvgiSignal",
  "seBands",
  "seBandsLower",
  "seBandsMiddle",
  "seBandsUpper",
  "seasAvg",
  "seasLose",
  "seasPath",
  "sectorStrength",
  "seasWin",
  // The `seas*` numbers: joined by qv-data from the yearly store (`seasonality_dsl`).
  "seasDev",
  "seasDnAvg",
  "seasEdge",
  "seasHit",
  "seasIn",
  "seasLeft",
  "seasMonEdge",
  "seasMonHit",
  "seasNext",
  "seasPval",
  "seasRun",
  "seasSharpe",
  "seasUpAvg",
  "seasVar",
  "seasWkEdge",
  "seasWkHit",
  "seasXsHit",
  "seasXsSharpe",
  "seasYears",
  "senkouA",
  "senkouB",
  "sharesDiluted",
  "shift",
  "signedAge",
  "sma",
  "stateAge",
  "stddev",
  "stoch",
  "stochD",
  "stochK",
  "stochRsi",
  "stochRsiD",
  "stochRsiK",
  "streak",
  "sum",
  "supertrend",
  "supertrendBuy",
  "supertrendDir",
  "supertrendDown",
  "supertrendSell",
  "supertrendUp",
  "tema",
  "tenkan",
  "trix",
  "trueRange",
  "tsi",
  "tsiSignal",
  "typicalPrice",
  "ulcer",
  "ultosc",
  "variance",
  "volumeOsc",
  "vortex",
  "vortexMinus",
  "vortexPlus",
  "vwap",
  "vwma",
  "wad",
  "wadHist",
  "wadMa",
  "weightedClose",
  "willr",
  "wma",
  "yearsToDouble",
  "zPrice",
  "zscore",
  "zVolume",
  // Sigma bands and their band measures (`qv-data/app/qf/expr_polars.py`).
  "bandPos",
  "bandWidth",
  "bandWidthP20",
  "bandWidthP80",
  "sigma",
  "sigmaAnchor",
  "sigmaAnnual",
  "sigmaAnnualMean",
  "sigmaLower1",
  "sigmaLower2",
  "sigmaLower3",
  "sigmaUpper1",
  "sigmaUpper2",
  "sigmaUpper3",
  "sigmaZ",
  "volSigma",
  // JAS and JP (ported from QuantVitals; `qv-data/app/qf/jas_jp.py`).
  "bongo",
  "wBongo",
  "dots",
  "guppy100",
  "guppy125",
  "guppy150",
  "guppy175",
  "guppy200",
  "guppy225",
  "guppy250",
  "guppy275",
  "guppy300",
  "guppy50",
  "guppy75",
  "dotsCpr",
  "dotsLR",
  "dotsEms",
  "dotsGap",
  "dotsGclRch",
  "dotsKf",
  "dotsMobo",
  "dotsPp",
  "dotsSar",
  "dotsSqFired",
  "dotsSqOn",
  "hcdLcd",
  "hj",
  "jasChandelier",
  "jasChandelierBuy",
  "jasChandelierLong",
  "jasChandelierSell",
  "jasChandelierShort",
  "jasGuppy",
  "jasObv",
  "jasObvEma",
  "jasTiming",
  "powerTrend",
  "ftd",
  "ftdDistribution",
  "ftdFollowThrough",
  "jpPivot",
  "jpPivotAvg",
  "jpPivotP",
  "jpPivotR1",
  "jpPivotR2",
  "jpPivotS1",
  "jpPivotS2",
  "jpDivergence",
  "jpdBearAge",
  "jpdBullAge",
  "lcc",
  "lccResistance",
  "lccSupport",
  "pps",
  "ppsBuy",
  "ppsSell",
  "pvh",
  "pvhHist",
  "pvhSignal",
]);

/**
 * Declared series functions a **screen has no route for**.
 *
 * `symbolClose("SPY")` and `compareTo(close, "SPY")` read a second symbol's
 * bars. No expression over these rows can produce that: they are resolved by
 * *joining a frame*, which is a chart's `data._foreign_columns` or a screen's
 * `screener_source.bind_foreign` — and both of those bind the **field** form
 * (`c@spy`). The call form is a chart indicator that happens to share the one
 * function registry the parser reads.
 *
 * So they are absent from `SERIES_FUNCTIONS` above, and that absence is
 * deliberate rather than an oversight — which is exactly why it needs a name.
 * Adding them there would route the column to a server with no lowering for it:
 * blank either way, and now a round trip to discover it.
 *
 * Two uses, both about *not offering what cannot work*:
 *
 * 1. `screenerCatalog` in `dsl-catalog.ts` filters them out of the injected
 *    `/dsl/catalog`, so completion and the reference cannot suggest one.
 * 2. `evalCall` names them specifically instead of saying "unknown function"
 *    about something the DSL plainly has — for an expression saved before the
 *    filter existed, or typed out in full.
 *
 * Listed by name rather than filtered by category, matching
 * `services/qv-data/tests/test_expr_polars.py`, which excludes the same ten:
 * the shared pin publishes names, arity and outputs and not categories, so
 * **adding a name has to be a decision**. A category filter would quietly cover
 * every future Symbol Indicator, including one a screen genuinely could answer.
 */
export const JOINED_ONLY_FUNCTIONS = new Set([
  "symbolSeries",
  "symbolOpen",
  "symbolHigh",
  "symbolLow",
  "symbolClose",
  "compareTo",
  "compareOpen",
  "compareHigh",
  "compareLow",
  "compareClose",
]);

/**
 * Market breadth — a count across a whole list, the same value on every row.
 *
 * Joined-only for now and for a different reason from the Symbol Indicators: a
 * screen *could* answer one (it is a market filter, `c.brAboveMA(SP500, 50) >
 * 60`), but the screener has no breadth join yet. Routed to the server today it
 * would come back blank, which is the silent failure `JOINED_ONLY_FUNCTIONS`
 * exists to prevent. `docs/breadth-design.html` § Screener.
 */
export const BREADTH_FUNCTIONS = new Set([
  "brAdv", "brDec", "brAd", "brPctAdv", "brAdr", "brUVol", "brDVol", "brUDV",
  "brUDVR", "brArms", "brMco", "brVolMco", "brNhl", "brNh", "brNl", "brNhlRatio",
  "brHlIndex", "brAboveMA", "brUp4", "brDn4", "brUp25", "brDn25", "brUp13", "brDn13",
  "brSecAD", "brSecADUSD", "brIndAD", "brIndADUSD", "brMacdPos",
  "jpMco", "jpMcoSignal", "jpMcoHist", "jpAdl", "jpAdlFast", "jpAdlSlow",
  "brAdl", "brSummation", "brVolSummation", "brUDVL",
  "jasEureka", "jasPhoenix", "jasPhoenixLN", "jasBongoUp", "jasBongoDown", "jasHindenburg", "jasTitanic", "jasDefib", "jasZweig",
]);
for (const name of BREADTH_FUNCTIONS) JOINED_ONLY_FUNCTIONS.add(name);

/**
 * The frequency an unqualified field reference means, and the only one a
 * snapshot row carries.
 *
 * A fourth copy of one letter — `DEFAULT_FREQUENCY` in
 * `services/quevee/dsl/expr_parser.py`, `BASE_FREQUENCY` in qv-data's
 * `app/qf/expr_identity.py`, `_BASE_FREQUENCY` in its `expr_polars` — because
 * none of the three runtimes can import from the others. Duplicated, not
 * derived, so changing what "daily" means is four deliberate edits.
 */
const DAILY_FREQUENCY = "d";

/**
 * `[count]unit`, the canonical spelling of `ScreenerFieldRef.frequency`. `mi`
 * is minute and `m` is month, so `mi` has to be tried first.
 *
 * Mirrors `_FREQUENCY_RE` in `expr_parser.py` **character for character, with
 * no case folding and no trimming**, because that parser does neither: an
 * off-canon `"D"` must be judged the same way on both sides. Both judge it
 * non-daily and route it to the server, which is the safe direction — see
 * `isDailyFrequency`.
 */
const FREQUENCY_RE = /^(\d*)(mi|s|h|d|w|m|q|y)$/;

/**
 * Is this ref's frequency one a snapshot row can answer?
 *
 * Three spellings mean daily and all three must pass: absent (the ref never
 * named one), `"d"`, and `"1d"` — `parse_frequency` normalises the last to the
 * middle, but a tree built by `<ExprCell>` or read back from a stored workspace
 * has been through no parser.
 *
 * Anything unrecognised is **not** treated as daily. A frequency this cannot
 * read is a frequency this cannot vouch for, and the two failures are not
 * symmetric: judged non-daily it goes to the server, which either answers it or
 * leaves the column blank; judged daily it gets a `valueGetter` that returns a
 * confident wrong number.
 */
function isDailyFrequency(frequency: string | undefined): boolean {
  if (!frequency) return true;
  const match = FREQUENCY_RE.exec(frequency);
  if (!match) return false;
  // `1d` and `d` are the same bar; `2d` is not, and two days of history is
  // history however short.
  return match[2] === DAILY_FREQUENCY && (match[1] === "" || match[1] === "1");
}

/** A problem found while evaluating; collected rather than thrown. */
export interface ExprIssue {
  kind: "unknownField" | "unknownFunction" | "needsHistory" | "arity";
  name: string;
  message: string;
}

export interface EvalOptions {
  /** Collects issues. Omit when only the value matters. */
  onIssue?: (issue: ExprIssue) => void;
}

const isLiteral = (e: ScreenerExpr): e is ScreenerExprLiteral => e.kind === "literal";
const isField = (e: ScreenerExpr): e is ScreenerExprField => e.kind === "field";
const isBinary = (e: ScreenerExpr): e is ScreenerExprBinary => e.kind === "binary";
const isCall = (e: ScreenerExpr): e is ScreenerExprCall => e.kind === "call";

/** Truthiness for `and`/`or` and for `iff`'s condition. Null is falsey. */
export function truthy(value: ScreenerValue): boolean {
  if (value == null) return false;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value !== 0 && !Number.isNaN(value);
  if (typeof value === "string") return value.length > 0;
  return true;
}

/** Coerce to a number for arithmetic. Null stays null so it can propagate. */
function num(value: ScreenerValue): number | null {
  if (value == null) return null;
  if (value instanceof Date) return value.getTime();
  const n = Number(value);
  return Number.isNaN(n) && typeof value !== "number" ? null : n;
}

/**
 * Compare two values. Strings compare lexically, everything else numerically,
 * so `sector == "Technology"` and `close > 5` both work without the expression
 * having to declare which it meant.
 */
function compare(left: ScreenerValue, right: ScreenerValue): number | null {
  if (left == null || right == null) return null;
  if (typeof left === "string" || typeof right === "string") {
    const a = String(left);
    const b = String(right);
    return a < b ? -1 : a > b ? 1 : 0;
  }
  const a = num(left);
  const b = num(right);
  if (a == null || b == null) return null;
  return a < b ? -1 : a > b ? 1 : 0;
}

function evalBinary(
  expr: ScreenerExprBinary,
  row: ScreenerRow,
  options: EvalOptions,
): ScreenerValue {
  // Short-circuit before evaluating the right side, so `a and b` skips `b` —
  // matching how the server's lowering will read, and cheaper on wide rows.
  if (expr.op === "and") {
    const left = evaluate(expr.left, row, options);
    return truthy(left) ? truthy(evaluate(expr.right, row, options)) : false;
  }
  if (expr.op === "or") {
    const left = evaluate(expr.left, row, options);
    return truthy(left) ? true : truthy(evaluate(expr.right, row, options));
  }

  const left = evaluate(expr.left, row, options);
  const right = evaluate(expr.right, row, options);

  switch (expr.op) {
    case "==":
      return left === right || compare(left, right) === 0;
    case "!=":
      return !(left === right || compare(left, right) === 0);
    case ">":
    case ">=":
    case "<":
    case "<=": {
      const c = compare(left, right);
      if (c == null) return null; // a null operand is not "less than" anything
      if (expr.op === ">") return c > 0;
      if (expr.op === ">=") return c >= 0;
      if (expr.op === "<") return c < 0;
      return c <= 0;
    }
    default:
      break;
  }

  const a = num(left);
  const b = num(right);
  if (a == null || b == null) return null; // propagate rather than coerce to 0

  switch (expr.op) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    // Division by zero is deliberately not an error: Polars yields inf, and the
    // grid renders and sorts it. Silently turning it into null would hide a real
    // value; throwing would blank the column.
    case "/":
      return a / b;
    case "%":
      return a % b;
    default:
      return null;
  }
}

function evalCall(
  expr: ScreenerExprCall,
  row: ScreenerRow,
  options: EvalOptions,
): ScreenerValue {
  const fn = expr.fn;

  if (SERIES_FUNCTIONS.has(fn)) {
    options.onIssue?.({
      kind: "needsHistory",
      name: fn,
      message:
        `${fn}() needs bar history; this row carries only the last bar. ` +
        `It is computed server-side, not here.`,
    });
    return null;
  }

  // `iff` evaluates lazily so the untaken branch cannot raise an issue for a
  // field it was never going to read.
  if (fn === "iff") {
    if (expr.args.length !== 3) {
      options.onIssue?.({
        kind: "arity",
        name: fn,
        message: `iff() takes 3 arguments, got ${expr.args.length}.`,
      });
      return null;
    }
    return truthy(evaluate(expr.args[0]!, row, options))
      ? evaluate(expr.args[1]!, row, options)
      : evaluate(expr.args[2]!, row, options);
  }

  const args = expr.args.map((a) => evaluate(a, row, options));

  switch (fn) {
    case "coalesce":
      return args.find((v) => v != null) ?? null;
    case "abs": {
      const n = num(args[0] ?? null);
      return n == null ? null : Math.abs(n);
    }
    case "round": {
      const n = num(args[0] ?? null);
      if (n == null) return null;
      const places = num(args[1] ?? null) ?? 0;
      const factor = 10 ** places;
      return Math.round(n * factor) / factor;
    }
    case "log10": {
      const n = num(args[0] ?? null);
      return n == null ? null : Math.log10(n);
    }
    case "min":
    case "max": {
      const nums = args.map(num).filter((n): n is number => n != null);
      if (nums.length === 0) return null;
      return fn === "min" ? Math.min(...nums) : Math.max(...nums);
    }
    default:
      options.onIssue?.({
        kind: "unknownFunction",
        name: fn,
        // A Symbol Indicator is a real function of the DSL, so "unknown" is a
        // lie the user can check — the reference lists it for charts. Say what
        // is actually true: this screen has no way to answer it, and point at
        // the field form, which it does.
        message: BREADTH_FUNCTIONS.has(fn)
          ? `${fn}() is market breadth, which a screen cannot use yet. Add it to a chart instead.`
          : JOINED_ONLY_FUNCTIONS.has(fn)
          ? `${fn}() reads another symbol's bars, which a screen cannot do. Use the field form instead — c@spy compares against SPY on the same bar.`
          : `Unknown function ${fn}(). Available: ${SCALAR_FUNCTIONS.join(", ")}.`,
      });
      return null;
  }
}

/**
 * Evaluate an expression against one row. **Never throws.**
 *
 * The guard below is not defensive noise: a schema-valid tree cannot have a
 * missing child, but this also runs against trees mid-edit in the builder and
 * against whatever a stored workspace happens to contain. Degrading to an empty
 * cell is always better than taking the grid down with it.
 */
export function evaluate(
  expr: ScreenerExpr,
  row: ScreenerRow,
  options: EvalOptions = {},
): ScreenerValue {
  if (expr == null || typeof expr !== "object" || !("kind" in expr)) return null;

  if (isLiteral(expr)) return expr.value;

  if (isField(expr)) {
    if (!(expr.field in row)) {
      options.onIssue?.({
        kind: "unknownField",
        name: expr.field,
        message: `Column ${expr.field} is not in this screen.`,
      });
      return null;
    }
    return row[expr.field] ?? null;
  }

  if (isBinary(expr)) return evalBinary(expr, row, options);
  if (isCall(expr)) return evalCall(expr, row, options);
  return null;
}

/** Evaluate as a boolean — the shape a filter needs. */
export function evaluateAsBoolean(
  expr: ScreenerExpr,
  row: ScreenerRow,
  options: EvalOptions = {},
): boolean {
  return truthy(evaluate(expr, row, options));
}

/**
 * Does this expression need bar history?
 *
 * True for a series call, and for the three parts of a field ref whose value is
 * not in the row: a non-zero lookback, **another symbol**, and **a frequency
 * other than daily**. A row carries this symbol's last *daily* bar and nothing
 * else. This is **placement, not permission**: false evaluates here in a
 * `valueGetter`, true is materialised server-side and arrives as an ordinary
 * column.
 *
 * The name is narrower than the question. What this really asks is "can one row
 * answer it?", and a foreign symbol cannot — `c@spy` needs SPY's bar, which is
 * not in AAPL's row under any name.
 *
 * **Each of the three is silent when got wrong, and in the same way.** Reported
 * false, the column gets a `valueGetter`, and `evaluate` below reads
 * `expr.field` while ignoring the whole of `expr.ref` — so `c@spy` renders the
 * row's *own* close and `c / c@spy` is exactly 1.0 for every symbol on every
 * day, and `h.w` renders today's daily high under a `W` header. Entirely
 * plausible on a screen. The browser-side twin of the failure
 * `screener_source.bind_foreign` exists to prevent on the server.
 *
 * Static, so a caller can decide *before* connecting which columns the server
 * has to compute. Mirrors `analyse()` in `services/quevee/dsl/expr_analysis.py`;
 * the two must agree or a column will be computed in neither place.
 */
export function needsHistory(expr: ScreenerExpr): boolean {
  if (expr == null || typeof expr !== "object" || !("kind" in expr)) return false;
  if (isField(expr)) {
    return (
      (expr.ref?.offset ?? 0) > 0 ||
      Boolean(expr.ref?.symbol) ||
      !isDailyFrequency(expr.ref?.frequency)
    );
  }
  if (isBinary(expr)) return needsHistory(expr.left) || needsHistory(expr.right);
  if (isCall(expr)) {
    return SERIES_FUNCTIONS.has(expr.fn) || expr.args.some(needsHistory);
  }
  return false;
}

/** Every field id an expression reads — the `requires[]` of a computed column. */
export function collectFields(expr: ScreenerExpr, into: Set<string> = new Set()): Set<string> {
  if (isField(expr)) into.add(expr.field);
  else if (isBinary(expr)) {
    collectFields(expr.left, into);
    collectFields(expr.right, into);
  } else if (isCall(expr)) {
    for (const arg of expr.args) collectFields(arg, into);
  }
  return into;
}

const PRECEDENCE: Record<string, number> = {
  or: 1,
  and: 2,
  "==": 3,
  "!=": 3,
  ">": 4,
  ">=": 4,
  "<": 4,
  "<=": 4,
  "+": 5,
  "-": 5,
  "*": 6,
  "/": 6,
  "%": 6,
};

/**
 * Print an expression as DSL text.
 *
 * A printer, not a parser — which is why the builder's preview pane works before
 * the compiler exists. Parenthesises only where precedence requires it, so the
 * output reads the way a person would have written it.
 */
export function printExpr(expr: ScreenerExpr, parentPrecedence = 0): string {
  if (isLiteral(expr)) {
    return typeof expr.value === "string" ? JSON.stringify(expr.value) : String(expr.value);
  }
  if (isField(expr)) {
    const offset = expr.ref?.offset ?? 0;
    return offset > 0 ? `${expr.field}[${offset}]` : expr.field;
  }
  if (isCall(expr)) {
    return `${expr.fn}(${expr.args.map((a) => printExpr(a)).join(", ")})`;
  }
  if (isBinary(expr)) {
    const precedence = PRECEDENCE[expr.op] ?? 0;
    const body = `${printExpr(expr.left, precedence)} ${expr.op} ${printExpr(expr.right, precedence + 1)}`;
    return precedence < parentPrecedence ? `(${body})` : body;
  }
  return "";
}
